import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  POST_PROMPT_STYLE_LABEL,
  postExcerpt,
  type PostPromptKind,
  type PostPromptStyle,
} from '@mg/shared';
import { api, actionErrorText } from '../../lib/api';
import { Spinner } from '../../components/ui/Spinner';
import { cn } from '../../lib/cn';
import type { PostContext } from './postContext';

/**
 * "Generate image prompt from your post": a row of style pills directly above the field it fills.
 *
 * One press drafts a prompt in that style from the post and puts it in the field, where it stays
 * editable. Nothing is generated and no daily image generation is spent: this writes words only.
 * Shown only when the window was opened from a post; without one there is nothing to draft from.
 */
export function PromptFromPostRow({
  context,
  kind,
  styles,
  label,
  onDrafted,
}: {
  context: PostContext;
  kind: PostPromptKind;
  styles: readonly PostPromptStyle[];
  label: string;
  onDrafted: (prompt: string) => void;
}) {
  const [drafted, setDrafted] = useState<PostPromptStyle | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** What is sent: a long post (a full blog post) is drafted from its opening paragraphs. */
  const excerpt = useMemo(() => postExcerpt(context.text), [context.text]);
  /** Aborted when the window closes, so a draft nobody will read is not paid for in full. */
  const inFlight = useRef<AbortController | null>(null);
  useEffect(() => () => inFlight.current?.abort(), []);

  const draft = useMutation({
    mutationFn: (style: PostPromptStyle) => {
      inFlight.current?.abort();
      const controller = new AbortController();
      inFlight.current = controller;
      return api.promptFromPost(
        { text: excerpt.text, platform: context.platform, kind, style },
        controller.signal,
      );
    },
    onSuccess: ({ prompt }, style) => {
      setError(null);
      setDrafted(style);
      onDrafted(prompt);
    },
    onError: (err) => {
      if ((err as Error).name === 'AbortError') return;
      setError(actionErrorText(err, 'We couldn’t draft a prompt from your post.'));
    },
  });

  const noText = context.text.trim().length === 0;

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-ink-strong" id={`${kind}-prompt-row-label`}>
        {label}
      </p>
      <div
        role="group"
        aria-labelledby={`${kind}-prompt-row-label`}
        className="flex flex-wrap gap-2"
      >
        {styles.map((style) => {
          const busy = draft.isPending && draft.variables === style;
          const on = drafted === style;
          return (
            <button
              key={style}
              type="button"
              aria-pressed={on}
              disabled={noText || draft.isPending}
              onClick={() => draft.mutate(style)}
              className={cn(
                'focus-ring inline-flex min-h-11 items-center gap-1.5 rounded-full border px-3 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50',
                on
                  ? 'border-action bg-action text-action-fg'
                  : 'border-line-input bg-card text-ink-strong hover:bg-hover',
              )}
            >
              {busy && <Spinner className="h-3.5 w-3.5" />}
              {POST_PROMPT_STYLE_LABEL[style]}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-ink-muted" role="status" aria-live="polite">
        {noText
          ? 'Add text to your post to draft a prompt from it.'
          : draft.isPending
            ? 'Drafting from your post…'
            : drafted && !error
              ? `${POST_PROMPT_STYLE_LABEL[drafted]} ${kind === 'abstract' ? 'style' : 'prompt'} drafted from ${excerpt.partial ? 'the opening of your post' : 'your post'}. Edit anything before you generate.`
              : ''}
      </p>
      {error && <p className="text-xs text-red-600 dark:text-error-dark">{error}</p>}
    </div>
  );
}
